import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, Minus, Plus, RotateCcw } from 'lucide-react'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import './golfHoleImageViewer.css'

// A modal sibling keeps the scorecard, voice review and all pending work mounted.
export default function GolfHoleImageViewer({ media, courseName, holeNumber, refreshImage, onClose }) {
  const { t } = useOptionalPrivanaI18n()
  const text = key => t(`golfImage.${key}`, { course: courseName, number: holeNumber })
  const dialog = useRef(null), canvas = useRef(null), back = useRef(null)
  const close = useRef(() => {})
  const [source, setSource] = useState(media.url)
  const [attempt, setAttempt] = useState(0)
  const [status, setStatus] = useState('loading')
  const [natural, setNatural] = useState({ width: 1, height: 1 })
  const [size, setSize] = useState({ width: 1, height: 1 })
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 })
  const current = useRef(transform), pointers = useRef(new Map()), lastTap = useRef(null)
  const alive = useRef(true), request = useRef(0)
  const fit = Math.min(size.width / natural.width, size.height / natural.height)
  const width = natural.width * fit, height = natural.height * fit
  const update = (scale, x = 0, y = 0) => {
    scale = Math.max(1, Math.min(5, scale))
    const boundX = Math.max(0, (width * scale - size.width) / 2)
    const boundY = Math.max(0, (height * scale - size.height) / 2)
    const next = { scale, x: Math.max(-boundX, Math.min(boundX, x)), y: Math.max(-boundY, Math.min(boundY, y)) }
    current.current = next
    setTransform(next)
  }
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
      current.current = { scale: 1, x: 0, y: 0 }
      setTransform(current.current)
      pointers.current.clear()
    })
    observer.observe(canvas.current)
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    alive.current = true
    const trigger = document.activeElement
    const body = document.body, root = document.documentElement
    const saved = { position: body.style.position, top: body.style.top, width: body.style.width, overflow: root.style.overflow }
    const scrollX = window.scrollX, scrollY = window.scrollY
    Object.assign(body.style, { position: 'fixed', top: `-${scrollY}px`, width: '100%' })
    root.style.overflow = 'hidden'
    dialog.current.showModal()
    back.current.focus({ preventScroll: true })
    // Same URL and router state: Back consumes only this overlay entry.
    const marker = `golf-image-${Date.now()}`
    history.pushState({ ...history.state, golfImageViewer: marker }, '')
    let closing = false, requested = false
    const pop = event => {
      if (closing) return
      closing = true
      event.stopImmediatePropagation()
      onClose()
    }
    window.addEventListener('popstate', pop, true)
    close.current = () => { if (!closing && !requested) { requested = true; history.back() } }
    let subscription
    if (globalThis.Capacitor?.isNativePlatform?.()) {
      import('@capacitor/app').then(({ App }) => App.addListener('backButton', () => close.current())).then(handle => {
        if (!alive.current) handle.remove()
        else subscription = handle
      }).catch(() => {})
    }
    return () => {
      alive.current = false
      subscription?.remove()
      window.removeEventListener('popstate', pop, true)
      dialog.current?.close()
      Object.assign(body.style, { position: saved.position, top: saved.top, width: saved.width })
      root.style.overflow = saved.overflow
      window.scrollTo(scrollX, scrollY)
      trigger?.focus?.({ preventScroll: true })
      // External navigation/unmount must not leave a viewer marker behind.
      if (history.state?.golfImageViewer === marker) {
        const { golfImageViewer, ...state } = history.state
        history.replaceState(state, '')
      }
    }
  }, [onClose])
  useEffect(() => {
    if (status !== 'loading' && status !== 'refreshing') return
    const timer = setTimeout(() => { request.current++; setStatus('error') }, 20000)
    return () => clearTimeout(timer)
  }, [status, attempt])
  const retry = async () => {
    setStatus('refreshing')
    const id = ++request.current
    try {
      const url = await refreshImage(holeNumber)
      if (!alive.current || request.current !== id) return
      setSource(url)
      setStatus('loading')
      setAttempt(value => value + 1)
    } catch { if (alive.current && request.current === id) setStatus('error') }
  }
  const down = event => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY, moved: false })
    if (pointers.current.size > 1) { lastTap.current = null; for (const p of pointers.current.values()) p.moved = true }
  }
  const move = event => {
    const previous = pointers.current.get(event.pointerId)
    if (!previous) return
    const points = [...pointers.current.values()]
    const before = points.length === 2 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0
    const dx = event.clientX - previous.x, dy = event.clientY - previous.y
    previous.x = event.clientX; previous.y = event.clientY
    previous.moved ||= Math.hypot(previous.x - previous.startX, previous.y - previous.startY) > 8
    const value = current.current
    if (points.length === 2 && before > 0) {
      const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)
      const scale = Math.max(1, Math.min(5, value.scale * distance / before))
      const rect = canvas.current.getBoundingClientRect()
      const cx = (points[0].x + points[1].x) / 2 - rect.left - rect.width / 2
      const cy = (points[0].y + points[1].y) / 2 - rect.top - rect.height / 2
      const ratio = scale / value.scale
      update(scale, cx - (cx - value.x - dx / 2) * ratio, cy - (cy - value.y - dy / 2) * ratio)
    } else if (points.length === 1) update(value.scale, value.x + dx, value.y + dy)
  }
  const up = event => {
    const point = pointers.current.get(event.pointerId)
    pointers.current.delete(event.pointerId)
    if (event.type === 'pointercancel' || !point || point.moved) { lastTap.current = null; return }
    const now = performance.now(), last = lastTap.current
    if (last && now - last.time < 320 && Math.hypot(point.x - last.x, point.y - last.y) < 30) {
      update(current.current.scale > 1 ? 1 : 2.5)
      lastTap.current = null
    } else lastTap.current = { time: now, x: point.x, y: point.y }
  }
  return createPortal(<dialog ref={dialog} className="mp-golf-image-viewer" aria-label={text('title')} onCancel={event => { event.preventDefault(); close.current() }} onPointerDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}>
    <header><button ref={back} type="button" onClick={() => close.current()}><ArrowLeft aria-hidden="true" /><span>{text('back')}</span></button><div><strong>{courseName}</strong><span>{text('hole')}</span></div></header>
    <div ref={canvas} className="mp-golf-image-viewer__canvas" onPointerDown={status === 'ready' ? down : undefined} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
      {(status === 'loading' || status === 'refreshing') && <p role="status">{text('loading')}</p>}
      {status === 'error' ? <div role="alert"><p>{text('error')}</p><button type="button" onClick={retry}>{text('retry')}</button></div> : status !== 'refreshing' && <img key={attempt} src={source} alt={text('description')} draggable="false" onLoad={event => { setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight }); setStatus('ready') }} onError={() => setStatus('error')} style={{ visibility: status === 'ready' ? 'visible' : 'hidden', width, height, transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})` }} />}
    </div>
    <footer aria-label={text('zoom')}><button type="button" aria-label={text('out')} disabled={status !== 'ready' || transform.scale <= 1} onClick={() => update(transform.scale / 1.5, transform.x / 1.5, transform.y / 1.5)}><Minus aria-hidden="true" /></button><button type="button" aria-label={text('reset')} disabled={status !== 'ready'} onClick={() => update(1)}><RotateCcw aria-hidden="true" /></button><button type="button" aria-label={text('in')} disabled={status !== 'ready' || transform.scale >= 5} onClick={() => update(transform.scale * 1.5, transform.x * 1.5, transform.y * 1.5)}><Plus aria-hidden="true" /></button></footer>
  </dialog>, document.body)
}
